import { css } from 'styled-components';

import { colors } from '../../../core';
import { AnimatedLabel, baseSmallLabelStyles } from './SelectInput.styles';
import { SelectInputProps } from './SelectInput.types';

export default ({ isError, showIndicatorAnimation = true }: SelectInputProps) => css`
  .react-select {
    &__input {
      color: ${({ theme }) => theme.selectColor || colors.ash};
      font-size: ${({ theme }) => theme.fontSize || '14px'};
      font-family: ${({ theme }) => theme.fontFamily || `Quicksand, sans-serif`};
    }

    &__multi-value__label {
      padding: 0 3px;
      p {
        padding: 0;
      }
    }

    &__control {
      min-height: ${({ theme }) => theme.selectInputHeight || '36px'};
      height: auto;
      cursor: pointer;
      border: ${({ theme }) =>
        `1px solid ${
          isError ? theme.errorColor : theme.selectTertiaryBorderColor || colors.smoke
        }`};
      border-radius: ${({ theme }) => theme.selectTertiaryBorderRadius || '3px'};
      background-color: ${({ theme }) => theme.selectTertiaryBackgroundColor || colors.white};
      padding: ${({ theme }) => theme.selectInputPadding || '3px 8px'};

      ${({ theme }) =>
        theme.selectShowOnlyBottomBorder &&
        `
          border-top: 0;
          border-left: 0;
          border-right: 0;
          border-radius: 0;
      `}

      &:hover {
        box-shadow: ${({ theme }) =>
          theme.selectHoverShadow || '0 2px 4px 0 rgba(217, 217, 217, 0.5)'};
        border-color: ${({ theme }) => theme.selectTertiaryBorderColor || colors.smoke};
        border-bottom-color: ${({ theme }) => theme.selectTertiaryBorderColor || colors.smoke};
      }

      &--is-focused,
      &--is-focused:hover {
        box-shadow: none;
        outline: 0;
        border-color: ${({ theme }) => theme.selectTertiaryFocusedBorderColor || colors.primary};
        border-bottom-color: ${({ theme }) =>
          theme.selectTertiaryFocusedBorderColor || colors.primary};

        .react-select__single-value {
          border-color: ${({ theme }) => theme.selectBorderColor || colors.charcoal};
        }

        & ~ ${AnimatedLabel} {
          ${baseSmallLabelStyles};
        }
      }

      &--is-disabled {
        background-color: ${({ theme }) =>
          theme.selectTertiaryDisabledBackgroundColor || colors.pearl};
        border-color: ${({ theme }) => theme.selectDisabledBorderColor || colors.cloud};
        border-bottom-color: ${({ theme }) => theme.selectDisabledBorderColor || colors.cloud};
        box-shadow: none;

        .react-select__single-value {
          color: ${({ theme }) => theme.selectDisabledColor || colors.smoke};
        }
      }

      &--is-disabled svg path {
        fill: ${({ theme }) => theme.selectDisabledColor || colors.smoke};
      }

      &--menu-is-open {
        .react-select__indicator {
          ${({ theme }) =>
            showIndicatorAnimation &&
            theme.selectShowIndicatorAnimation &&
            `
            transform: rotate(-180deg);
            transition: transform 0.3s;
          `}
        }
      }
    }

    &__value-container {
      padding: 3px 8px;
      height: 34px;
      &--is-multi {
        height: auto;
      }
    }

    &__placeholder,
    &__single-value,
    &__option {
      font-family: ${({ theme }) => theme.fontFamily || `Quicksand, sans-serif`};
      font-size: ${({ theme }) => theme.fontSize || '14px'};
      line-height: ${({ theme }) => theme.fontLineHeight || '1.5'};
      font-weight: ${({ theme }) => theme.fontWeight || `600`};
    }

    &__placeholder {
      color: ${({ theme }) => theme.selectPlaceholderColor || colors.smoke};
    }

    &__single-value {
      color: ${({ theme }) => theme.selectColor || colors.ash};
    }

    &__indicator-separator {
      background-color: transparent;
      width: 8px;
    }

    &__indicator {
      padding: ${({ theme }) => theme.selectTertiaryIndicatorPadding || '7px 8px'};
      ${({ theme }) =>
        showIndicatorAnimation &&
        theme.selectShowIndicatorAnimation &&
        `
        transform: rotate(0);
        transition: transform 0.3s;
      `}
    }

    &__menu {
      border-radius: ${({ theme }) =>
        theme.selectTertiaryBorderRadius
          ? `0 0 ${theme.selectTertiaryBorderRadius} ${theme.selectTertiaryBorderRadius}`
          : '0 0 3px 3px'};
      margin-top: 0;
      overflow: hidden;
      transform: translateZ(0);
      display: block;
      box-shadow: ${({ theme }) => theme.selectMenuShadow || 'none'};
      border: ${({ theme }) => theme.selectMenuBorder || `1px solid ${colors.cloud}`};
      border-top: 0;

      &-notice--no-options {
        color: ${({ theme }) => theme.fontColor || colors.ash};
        font-size: ${({ theme }) => theme.fontSize || '14px'};
        font-family: ${({ theme }) => theme.fontFamily || `Quicksand, sans-serif`};
        padding: 16px 10px;
        display: none;
      }
    }

    &__menu-list {
      padding: 0;

      &--is-multi {
        label {
          line-height: ${({ theme }) => theme.selectOptionMultiLineHeight || '1'};
          margin-top: ${({ theme }) => theme.selectOptionMultiMargin || '0'};
        }
      }
    }

    &__option {
      background-color: ${colors.white};
      text-align: left;
      color: ${({ theme }) => theme.fontColor || colors.ash};
      cursor: pointer;
      padding: ${({ theme }) => theme.selectOptionPadding || '16px 10px'};

      &--is-focused {
        background-color: ${colors.pearl};
      }

      &:active {
        background-color: ${colors.cloud};
      }

      &--is-selected,
      &--is-selected:active {
        font-weight: 600;
        color: ${({ theme }) => theme.selectOptionActiveColor || colors.charcoal};
      }
    }

    &__group-heading {
      color: ${({ theme }) => theme.selectColor || colors.charcoal};
      font-size: 12px;
      font-family: ${({ theme }) => theme.fontFamily || `Quicksand, sans-serif`};
      padding: 8px 20px;
      font-weight: bold;
    }
  }
`;
